import { paths } from "@/config/paths";
import useCurrentUser from "@/store/useCurrentUser";
import React from "react";
import { Navigate } from "react-router-dom";
import { hasPermission } from "@/shared/permission-checker";
import { CLIENT_ROLES } from "@/types/user";
import type { Resource, Action } from "@/types/permissions";

interface ProtectedRouteProps {
  children: React.ReactNode;
  resource: Resource;
  action: Action;
}

export const ProtectedRoute = ({ children, resource, action }: ProtectedRouteProps) => {
  const user = useCurrentUser((state: any) => state.user);

  if (!user?.userId) return <Navigate to={paths.auth.login.path} />;

  if (!hasPermission(user.role, resource, action)) return <Navigate to={paths.home.path} />;

  return children;
};

interface CanAccessProps {
  resource: Resource;
  action: Action;
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

export const CanAccess = ({ resource, action, children, fallback = null }: CanAccessProps) => {
  const user = useCurrentUser((state: any) => state.user);

  if (!user?.userId || !hasPermission(user.role, resource, action)) return <>{fallback || <></>}</>;

  return <>{children}</>;
};

/**
 * Guard that only allows client-portal roles (client_viewer / client_decision_maker).
 * Used to wrap the entire /portal/* surface so a client can never land on an
 * employee-only route and vice-versa.
 */
export const ClientRoute = ({ children }: { children: React.ReactNode }) => {
  const user = useCurrentUser((state: any) => state.user);

  if (!user?.userId) return <Navigate to={paths.auth.login.path} />;

  if (!CLIENT_ROLES.includes(user.role)) return <Navigate to={paths.home.path} />;

  return <>{children}</>;
};

/**
 * Guard that only allows employee roles (everyone except client-portal roles).
 * Wraps the entire /app/* employee surface so a client can never accidentally
 * land on admin software — they are bounced to the client portal instead.
 */
export const EmployeeRoute = ({ children }: { children: React.ReactNode }) => {
  const user = useCurrentUser((state: any) => state.user);

  if (!user?.userId) return <Navigate to={paths.auth.login.path} />;

  if (CLIENT_ROLES.includes(user.role)) {
    return <Navigate to={paths.app.portal.getHref()} />;
  }

  return <>{children}</>;
};
